import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  SubmissionsReasonCode as ReasonCode,
  TenantsCommunityRequirement as CommunityRequirement,
  TenantsCorrelationKind as CorrelationKind,
  TenantsCorrelationKindCreate as CorrelationKindCreate,
  TenantsPublicKey as PublicKey,
  TenantsReplyTemplate as ReplyTemplate,
  TenantsReplyTemplateCreate as ReplyTemplateCreate,
  TenantsTenantSettings as TenantSettings,
  TenantsTenantSettingsUpdate as TenantSettingsUpdate,
  TenantsTextMaxLength as TextMaxLength,
} from '../generated/api/types.gen.ts';
import { settingsGet, settingsUpdate } from '../generated/api/sdk.gen.ts';
import { ApiFeil } from './feil.ts';
import { useApiKlient } from './klient.ts';
import { hypoteseNoekkel } from './useHypotheses.ts';

export type { CommunityRequirement, CorrelationKind, CorrelationKindCreate, PublicKey, ReasonCode, ReplyTemplate, ReplyTemplateCreate, TenantSettings, TenantSettingsUpdate, TextMaxLength };

/** Nøkkelen eies her (`react-ts-integration`). Innstillingsflaten (M2.5) skriver den etter PATCH. */
export const innstillingerNoekkel = ['settings'] as const;

/**
 * `GET /v1/admin/settings` — svarmalene og fristen (M2.3), hele innstillingsflaten (M2.5).
 * Leses én gang per sesjon; PATCH legger svaret rett i cachen.
 */
export function useInnstillinger() {
  const klient = useApiKlient();
  return useQuery({
    queryKey: innstillingerNoekkel,
    staleTime: Infinity,
    queryFn: async (): Promise<TenantSettings> => {
      const res = await settingsGet({ client: klient });
      if (!res.response?.ok || !res.data) throw ApiFeil.fra(res.response?.status ?? 0, res.error ?? res.data);
      return res.data as TenantSettings;
    },
  });
}

/** `PATCH /v1/admin/settings` — kun eier. Bare de feltene som er endret sendes; utelatt betyr «ikke rør». */
export function useOppdaterInnstillinger() {
  const klient = useApiKlient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: TenantSettingsUpdate): Promise<TenantSettings> => {
      const res = await settingsUpdate({ client: klient, body });
      if (!res.response?.ok || !res.data) throw ApiFeil.fra(res.response?.status ?? 0, res.error ?? res.data);
      return res.data as TenantSettings;
    },
    onSuccess: (innstillinger, body) => {
      queryClient.setQueryData(innstillingerNoekkel, innstillinger);
      // Rommet Hypoteser leser sine innstillinger gjennom `GET /hypotheses/config` (H2.3): en lagring
      // av `hypothesis*` gjør den foreldet — ordlyden, fristene og beslutningstakerne er de samme.
      if (Object.keys(body).some((k) => k.startsWith('hypothesis'))) void queryClient.invalidateQueries({ queryKey: hypoteseNoekkel.config });
    },
  });
}
